/**
 * Minimal markdown renderer for the Word add-in (webpack/CommonJS environment).
 * Handles the subset Gemini typically produces:
 *   headings (#, ##, ###), bold (**), italic (*_), inline code (`),
 *   fenced code blocks (```), unordered lists (- / *), ordered lists (1.),
 *   blockquotes (>), horizontal rules (---), tables (| a | b |).
 * An answer that points at its sources as [1], [2] gets each pointer drawn as
 * a tag that names the source (`sources`); a tag whose source can be opened is
 * a button (`onOpenSource`).
 */
import * as React from 'react';
import type { PlaybookArchiveRow } from '../../utils/playbookRecordIndex';
import { resolveCitationRecordNum } from '../../utils/playbookRecordIndex';
import type { PlaybookRecordPreviewData } from './PlaybookRecordClausePreview';
import { RecordCitationLink } from './RecordCitationLink';

/* ── helpers ─────────────────────────────────────────── */

/**
 * Matches a citation phrase introducing one or more archive record numbers,
 * across the formats the model actually produces in English and Hebrew:
 *
 *   Record 5            Records 5, 6, 7
 *   רשומה 5             רשומות 5, 6, 7
 *   [[Record 5]]        [[Record 5, 6, 7]]   ← explicit stable token
 *
 * Group 1 captures the trailing numeric list (e.g. "5", "5, 6, 7"); we then
 * split it and emit one clickable chip per number.
 */
/** No \\b — JS word boundaries do not work with Hebrew letters. */
const PLAYBOOK_RECORD_REF_RE =
  /(?:\[\[\s*(?:Records?|רשומ(?:ה|ות))\s+([\d, ]+?)\s*\]\]|(?:Records?|רשומ(?:ה|ות))\s+(\d+(?:\s*[,،]\s*\d+)*))/gu;

const PLAYBOOK_MATCH_REF_RE =
  /(?:\[\[\s*(?:Matches?|התאמה(?:ות)?)\s+([\d, ]+?)\s*\]\]|(?:Matches?|התאמה(?:ות)?)\s+(\d+(?:\s*[,،]\s*\d+)*))/gu;

export type AskMarkdownRecordLinkOptions = {
  validRecordNums?: Set<number>;
  onRecordClick?: (recordNum: number) => void;
  getRecord?: (recordNum: number) => PlaybookArchiveRow | undefined;
  loadPreview?: (row: PlaybookArchiveRow) => Promise<PlaybookRecordPreviewData>;
  /** Match rank → archive record number (fixes Record N vs Match N confusion). */
  matchRankToArchiveRecordNum?: Map<number, number>;
};

/** A source an answer points at as `[n]`: the file, the place in it, and the words quoted from there. */
export type AskMarkdownSource = { n: number; filename: string; location?: string; quote?: string; fullText?: string };

/** Opening a source from its tag: which sources can be opened, and what a click does. */
export type AskMarkdownSourceOpener = {
  canOpen: (source: AskMarkdownSource) => boolean;
  open: (source: AskMarkdownSource) => void;
  /** Said on the tag of a source that can be opened. */
  label: string;
};

type InlineOptions = {
  recordLinks?: AskMarkdownRecordLinkOptions;
  sources?: AskMarkdownSource[];
  openSource?: AskMarkdownSourceOpener;
};

function isHebrew(text: string): boolean {
  return /[\u0590-\u05FF]/.test(text);
}

function parseRecordNumbers(match: string, body: string): number[] {
  const nums: number[] = [];
  for (const part of body.split(/[,،\s]+/)) {
    const n = parseInt(part, 10);
    if (Number.isFinite(n)) nums.push(n);
  }
  if (nums.length === 0) {
    const m = match.match(/\d+/g);
    if (m) for (const s of m) {
      const n = parseInt(s, 10);
      if (Number.isFinite(n)) nums.push(n);
    }
  }
  return nums;
}

type CitationHit = {
  index: number;
  length: number;
  citedNums: number[];
  archiveNums: number[];
  hebrew: boolean;
  kind: 'record' | 'match';
};

function findCitationHits(
  raw: string,
  recordLinks: AskMarkdownRecordLinkOptions,
): CitationHit[] {
  const validNums = recordLinks.validRecordNums!;
  const matchMap = recordLinks.matchRankToArchiveRecordNum;
  const hits: CitationHit[] = [];

  const recordRe = new RegExp(PLAYBOOK_RECORD_REF_RE.source, PLAYBOOK_RECORD_REF_RE.flags);
  let m: RegExpExecArray | null;
  while ((m = recordRe.exec(raw)) !== null) {
    const fullMatch = m[0];
    const numberBody = m[1] ?? m[2] ?? '';
    const citedNums = parseRecordNumbers(fullMatch, numberBody);
    const archiveNums = citedNums
      .map((n) => resolveCitationRecordNum(n, matchMap))
      .filter((n) => validNums.has(n));
    if (!archiveNums.length) continue;
    hits.push({
      index: m.index,
      length: fullMatch.length,
      citedNums,
      archiveNums,
      hebrew: isHebrew(fullMatch),
      kind: 'record',
    });
  }

  if (matchMap?.size) {
    const matchRe = new RegExp(PLAYBOOK_MATCH_REF_RE.source, PLAYBOOK_MATCH_REF_RE.flags);
    while ((m = matchRe.exec(raw)) !== null) {
      const fullMatch = m[0];
      const numberBody = m[1] ?? m[2] ?? '';
      const citedNums = parseRecordNumbers(fullMatch, numberBody);
      const archiveNums = citedNums
        .map((rank) => matchMap.get(rank))
        .filter((n): n is number => n != null && validNums.has(n));
      if (!archiveNums.length) continue;
      hits.push({
        index: m.index,
        length: fullMatch.length,
        citedNums,
        archiveNums,
        hebrew: isHebrew(fullMatch),
        kind: 'match',
      });
    }
  }

  hits.sort((a, b) => a.index - b.index);
  return hits;
}

/** Convert inline markdown; linkify record citations in English/Hebrew & `[[Record N]]` tokens. */
function inlineToReact(raw: string, key: string | number, options?: InlineOptions): React.ReactNode {
  const sources = options?.sources;
  if (sources?.length) {
    // "[2]" becomes a tag that names its source; a number no source has stays as it was written.
    const parts: React.ReactNode[] = [];
    const pointer = /\[(\d{1,3})\]/g;
    let last = 0;
    let seg = 0;
    let found: RegExpExecArray | null;
    while ((found = pointer.exec(raw)) !== null) {
      const source = sources.find((item) => item.n === Number(found![1]));
      if (!source) continue;
      if (found.index > last) {
        parts.push(
          <React.Fragment key={`${key}-st${seg++}`}>
            {inlineWithRecords(raw.slice(last, found.index), `${key}-ss${seg}`, options?.recordLinks)}
          </React.Fragment>,
        );
      }
      const named = source.location ? `${source.filename} · ${source.location}` : source.filename;
      const opener = options?.openSource;
      parts.push(
        opener?.canOpen(source) ? (
          <button
            key={`${key}-sp${seg++}`}
            type="button"
            className="ask-md-source-ref ask-md-source-ref--open"
            data-source={source.n}
            title={`${named}\n${opener.label}`}
            onClick={() => opener.open(source)}
          >
            {source.n}
          </button>
        ) : (
          <span key={`${key}-sp${seg++}`} className="ask-md-source-ref" data-source={source.n} title={named}>
            {source.n}
          </span>
        ),
      );
      last = found.index + found[0].length;
    }
    if (parts.length) {
      if (last < raw.length) {
        parts.push(
          <React.Fragment key={`${key}-st${seg++}`}>
            {inlineWithRecords(raw.slice(last), `${key}-se${seg}`, options?.recordLinks)}
          </React.Fragment>,
        );
      }
      return <>{parts}</>;
    }
  }
  return inlineWithRecords(raw, key, options?.recordLinks);
}

function inlineWithRecords(
  raw: string,
  key: string | number,
  recordLinks?: AskMarkdownRecordLinkOptions,
): React.ReactNode {
  if (recordLinks?.onRecordClick && recordLinks.validRecordNums?.size) {
    const hits = findCitationHits(raw, recordLinks);
    if (hits.length) {
      const parts: React.ReactNode[] = [];
      let last = 0;
      let seg = 0;

      const renderLink = (archiveNum: number, linkLabel: React.ReactNode, linkKey: string) => {
        const row = recordLinks.getRecord?.(archiveNum);
        if (row && recordLinks.loadPreview) {
          return (
            <RecordCitationLink
              key={linkKey}
              recordNum={archiveNum}
              label={linkLabel}
              row={row}
              loadPreview={recordLinks.loadPreview}
              onOpenSource={recordLinks.onRecordClick!}
            />
          );
        }
        return (
          <button
            key={linkKey}
            type="button"
            className="ask-md-record-link"
            onClick={() => recordLinks.onRecordClick!(archiveNum)}
          >
            {linkLabel}
          </button>
        );
      };

      for (const hit of hits) {
        if (hit.index > last) {
          parts.push(
            <React.Fragment key={`${key}-t${seg++}`}>
              {inlineToReactCore(raw.slice(last, hit.index), `${key}-s${seg}`)}
            </React.Fragment>,
          );
        }

        if (hit.archiveNums.length === 1) {
          const cited = hit.citedNums[0];
          const archiveNum = hit.archiveNums[0];
          const label =
            hit.kind === 'match'
              ? hit.hebrew
                ? `התאמה ${cited}`
                : `Match ${cited}`
              : hit.hebrew
                ? `רשומה ${cited}`
                : `Record ${cited}`;
          parts.push(renderLink(archiveNum, label, `${key}-r${seg++}`));
        } else {
          const prefix =
            hit.kind === 'match'
              ? hit.hebrew
                ? 'התאמות'
                : 'Matches'
              : hit.hebrew
                ? 'רשומות'
                : 'Records';
          parts.push(
            <span key={`${key}-r${seg++}`} className="ask-md-record-link-group">
              <span className="ask-md-record-link-prefix">{prefix} </span>
              {hit.archiveNums.map((archiveNum, idx) => (
                <React.Fragment key={`${key}-r${seg}-${idx}`}>
                  {renderLink(archiveNum, hit.citedNums[idx] ?? archiveNum, `${key}-r${seg}-${idx}`)}
                  {idx < hit.archiveNums.length - 1 ? <span>, </span> : null}
                </React.Fragment>
              ))}
            </span>,
          );
        }

        last = hit.index + hit.length;
      }

      if (last < raw.length) {
        parts.push(
          <React.Fragment key={`${key}-t${seg++}`}>
            {inlineToReactCore(raw.slice(last), `${key}-e${seg}`)}
          </React.Fragment>,
        );
      }
      return <>{parts}</>;
    }
  }

  return inlineToReactCore(raw, key);
}

function inlineToReactCore(raw: string, key: string | number): React.ReactNode {
  const parts: React.ReactNode[] = [];
  // inline code, bold+italic, bold, italic
  const pattern = /(`[^`]+`|\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|__(.+?)__|_(.+?)_|\*(.+?)\*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;

  while ((m = pattern.exec(raw)) !== null) {
    if (m.index > last) {
      parts.push(<React.Fragment key={`t${key}-${i++}`}>{raw.slice(last, m.index)}</React.Fragment>);
    }
    const token = m[0];
    if (token.startsWith('`')) {
      parts.push(<code key={`c${key}-${i++}`} className="ask-md-code">{token.slice(1, -1)}</code>);
    } else if (token.startsWith('***')) {
      parts.push(<strong key={`bi${key}-${i++}`}><em>{m[2]}</em></strong>);
    } else if (token.startsWith('**') || token.startsWith('__')) {
      parts.push(<strong key={`b${key}-${i++}`}>{m[3] ?? m[4]}</strong>);
    } else {
      parts.push(<em key={`em${key}-${i++}`}>{m[5] ?? m[6]}</em>);
    }
    last = m.index + token.length;
  }
  if (last < raw.length) {
    parts.push(<React.Fragment key={`t${key}-${i++}`}>{raw.slice(last)}</React.Fragment>);
  }
  return <>{parts}</>;
}

type Block =
  | { type: 'h1' | 'h2' | 'h3'; text: string }
  | { type: 'p'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'blockquote'; text: string }
  | { type: 'hr' }
  | { type: 'table'; head: string[]; rows: string[][] }
  | { type: 'code'; lang: string; text: string };

/** The cells of one line of a table ("| a | b |"). */
function tableCells(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());
}

function isTableRule(line: string | undefined): boolean {
  return !!line && /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line) && line.includes('-');
}

function parseBlocks(md: string): Block[] {
  const lines = md.split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // fenced code block
    if (/^```/.test(line)) {
      const lang = line.slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // closing ```
      blocks.push({ type: 'code', lang, text: codeLines.join('\n') });
      continue;
    }

    // heading
    if (/^### /.test(line)) { blocks.push({ type: 'h3', text: line.slice(4) }); i++; continue; }
    if (/^## /.test(line))  { blocks.push({ type: 'h2', text: line.slice(3) }); i++; continue; }
    if (/^# /.test(line))   { blocks.push({ type: 'h1', text: line.slice(2) }); i++; continue; }

    // table: a line of cells, a rule under it, and the rows
    if (line.includes('|') && isTableRule(lines[i + 1])) {
      const head = tableCells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].includes('|') && lines[i].trim() !== '') {
        rows.push(tableCells(lines[i]));
        i++;
      }
      blocks.push({ type: 'table', head, rows });
      continue;
    }

    // horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) { blocks.push({ type: 'hr' }); i++; continue; }

    // blockquote
    if (/^> /.test(line)) {
      const quoteLines: string[] = [];
      while (i < lines.length && /^> /.test(lines[i])) {
        quoteLines.push(lines[i].slice(2));
        i++;
      }
      blocks.push({ type: 'blockquote', text: quoteLines.join('\n') });
      continue;
    }

    // unordered list
    if (/^[-*+] /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*+] /.test(lines[i])) {
        items.push(lines[i].replace(/^[-*+] /, ''));
        i++;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    // ordered list
    if (/^\d+\. /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\. /, ''));
        i++;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    // blank line — skip
    if (line.trim() === '') { i++; continue; }

    // paragraph — accumulate non-blank lines
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^(#{1,3} |```|> |[-*+] |\d+\. )/.test(lines[i]) &&
      !(paraLines.length > 0 && lines[i].includes('|') && isTableRule(lines[i + 1]))
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length) {
      blocks.push({ type: 'p', text: paraLines.join(' ') });
    }
  }

  return blocks;
}

/* ── component ───────────────────────────────────────── */

interface AskMarkdownProps {
  content: string;
  dir?: 'rtl' | 'ltr';
  recordLinks?: AskMarkdownRecordLinkOptions;
  /** The sources the text points at as [1], [2]: each pointer is drawn as a tag that names its source. */
  sources?: AskMarkdownSource[];
  /** A tag whose source can be opened is a button that opens it. */
  openSource?: AskMarkdownSourceOpener;
}

export const AskMarkdown: React.FC<AskMarkdownProps> = ({
  content,
  dir = 'ltr',
  recordLinks,
  sources,
  openSource,
}) => {
  const blocks = React.useMemo(() => parseBlocks(content), [content]);
  const linkOpts: InlineOptions | undefined = recordLinks || sources?.length ? { recordLinks, sources, openSource } : undefined;

  return (
    <div className={`ask-markdown-content ${dir}`} dir={dir}>
      {blocks.map((block, idx) => {
        switch (block.type) {
          case 'h1': return <h1 key={idx}>{inlineToReact(block.text, idx, linkOpts)}</h1>;
          case 'h2': return <h2 key={idx}>{inlineToReact(block.text, idx, linkOpts)}</h2>;
          case 'h3': return <h3 key={idx}>{inlineToReact(block.text, idx, linkOpts)}</h3>;
          case 'hr': return <hr key={idx} className="ask-md-hr" />;
          case 'p':  return <p key={idx}>{inlineToReact(block.text, idx, linkOpts)}</p>;
          case 'blockquote':
            return (
              <blockquote key={idx} className="ask-md-blockquote">
                {inlineToReact(block.text, idx, linkOpts)}
              </blockquote>
            );
          case 'ul':
            return (
              <ul key={idx}>
                {block.items.map((item, j) => (
                  <li key={j}>{inlineToReact(item, `${idx}-${j}`, linkOpts)}</li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={idx}>
                {block.items.map((item, j) => (
                  <li key={j}>{inlineToReact(item, `${idx}-${j}`, linkOpts)}</li>
                ))}
              </ol>
            );
          case 'table':
            return (
              <div key={idx} className="ask-md-table-wrap">
                <table className="ask-md-table">
                  <thead>
                    <tr>
                      {block.head.map((cell, j) => (
                        <th key={j}>{inlineToReact(cell, `${idx}-h${j}`, linkOpts)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row, r) => (
                      <tr key={r}>
                        {block.head.map((_, j) => (
                          <td key={j}>{inlineToReact(row[j] ?? '', `${idx}-${r}-${j}`, linkOpts)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case 'code':
            return (
              <pre key={idx} className="ask-md-pre">
                <code>{block.text}</code>
              </pre>
            );
          default: return null;
        }
      })}
    </div>
  );
};
